// Role + transition gating for the logistics-requests UI.
//
// Three layers (after #445 — Phase 2 of `requests.scope.all`):
//   1. State machine — `REQUEST_TRANSITIONS` from packages/shared decides
//      which transitions are legal from which `RequestStatus`.
//   2. RBAC — every transition declares one or more `PermissionCode`s; an
//      action button is hidden when the user's permissions bundle does
//      not satisfy them.
//   3. Party — visibility (`requests.scope.all`) and write authority are
//      decoupled. The backend chokepoint
//      (`apps/backend/src/modules/requests/transitions.service.ts`)
//      additionally requires the actor to be the matching party for the
//      side a transition runs on (TRANSITION_SIDE map). Without a
//      client-side party gate, a logist_cn (scope='all') would see Send
//      / Close buttons on a foreign manager's row and only learn the
//      403 after the round-trip. The check mirrors the backend exactly,
//      with the same admin (`permissions === 'ALL'`) bypass — so the UI
//      collapses the action set without changing the chokepoint's
//      authority.
//
// `RoleKind` is a UI-only convenience driving role-aware page copy. It is
// derived from `User.role.name`; unknown role names fall back to 'manager'.

import {
  REQUEST_TRANSITIONS,
  RequestStatus,
  hasPermission,
  type PermissionCode,
  type TransitionKey,
} from '@magimp/shared';
import type { IconName } from '../../ui/index.js';
import type { PermissionsBundle } from '../../stores/auth.js';

export interface RequestFacts {
  hasAnsweredCarrier?: boolean;
}

export type TransitionLabelKey =
  | 'actions.send'
  | 'actions.return'
  | 'actions.process'
  | 'actions.reopen'
  | 'actions.close'
  | 'actions.archive'
  | 'actions.unarchive'
  | 'actions.success'
  | 'actions.refuse'
  | 'actions.cannotTransport'
  | 'actions.postpone';

// #1164: when the action is surfaced but cannot be invoked yet, the
// drawer renders a disabled button with this tooltip. The reason is
// emitted alongside `disabled` so consumers can localize without
// inspecting facts again.
export type TransitionDisabledReasonKey = 'actions.processDisabledNoAnswered';

export interface TransitionAction {
  key: TransitionKey;
  to: RequestStatus;
  labelKey: TransitionLabelKey;
  icon: IconName;
  kind: 'primary' | 'secondary' | 'danger';
  needsReason?: boolean;
  needsConfirm?: boolean;
  disabled?: boolean;
  disabledReasonKey?: TransitionDisabledReasonKey;
}

const ACTION_META: Record<TransitionKey, Omit<TransitionAction, 'key' | 'to'>> = {
  send: { labelKey: 'actions.send', icon: 'send-horizontal', kind: 'primary' },
  return: {
    labelKey: 'actions.return',
    icon: 'rotate-ccw',
    kind: 'danger',
    needsReason: true,
  },
  process: { labelKey: 'actions.process', icon: 'check-check', kind: 'primary' },
  reopen: {
    labelKey: 'actions.reopen',
    icon: 'rotate-ccw',
    kind: 'secondary',
    needsReason: true,
  },
  close: { labelKey: 'actions.close', icon: 'lock', kind: 'primary', needsConfirm: true },
  archive: { labelKey: 'actions.archive', icon: 'archive', kind: 'secondary' },
  unarchive: { labelKey: 'actions.unarchive', icon: 'archive-restore', kind: 'secondary' },
  success: {
    labelKey: 'actions.success',
    icon: 'check-check',
    kind: 'primary',
    needsConfirm: true,
  },
  refuse: { labelKey: 'actions.refuse', icon: 'x', kind: 'danger', needsReason: true },
  // WHY (#1836): parking is a neutral, reversible action — secondary tone,
  //   no modal (routes through a direct no-body mutation in RequestDrawer's
  //   onAction). `clock` reads as "parked / pending decision".
  postpone: { labelKey: 'actions.postpone', icon: 'clock', kind: 'secondary' },
  // WHY (#2139): the China Logist's terminal «cannot transport» action.
  //   Danger tone + required reason (routed through `CannotTransportModal`)
  //   + confirm — distinct from a customer REFUSED.
  cannotTransport: {
    labelKey: 'actions.cannotTransport',
    icon: 'x',
    kind: 'danger',
    needsReason: true,
    needsConfirm: true,
  },
};

// INVARIANT: mirrors `TRANSITION_SIDE` in
// `apps/backend/src/modules/requests/transitions.service.ts`. A divergence
// here would let the UI render a button the backend will reject, defeating
// the point of party-aware gating. Drift detection is structural — the
// `Record<TransitionKey, ...>` shape forces every transition key to be
// listed, so adding a new transition surfaces here as a TS error.
const TRANSITION_SIDE: Record<TransitionKey, 'manager' | 'logist' | 'either'> = {
  send: 'manager',
  return: 'either',
  process: 'logist',
  reopen: 'logist',
  close: 'manager',
  archive: 'either',
  unarchive: 'either',
  success: 'manager',
  refuse: 'manager',
  // WHY (#1836): parking is a manager-side commercial call — mirrors the
  //   backend `TRANSITION_SIDE.postpone = 'manager'`.
  postpone: 'manager',
  // WHY (#2133/#2139): «cannot transport» is logist-side — mirrors the backend
  //   `TRANSITION_SIDE.cannotTransport = 'logist'`. Now consulted: the action
  //   surfaces for the assigned logist from IN_WORK (the manager never sees it).
  cannotTransport: 'logist',
};

/**
 * @aiPurpose party context for a single request row, supplied by the
 *   caller so {@link allowedActions} and {@link canPickWinner} can apply
 *   the same write-authority gate the backend chokepoint enforces.
 * @aiInvariant `actorUserId` is the authenticated user's id (UUID) or
 *   null when no session — null forces a no-action result for non-admin
 *   actors (defensive). `managerId`/`logistId` are the row's owning
 *   parties (logist may be null on draft / unassigned rows). The
 *   `permissions === 'ALL'` admin bypass is derived from the bundle
 *   inside the helpers, not from a flag on this struct, to keep the
 *   "client mirrors backend" predicate single-sourced.
 */
export interface ActorPartyContext {
  actorUserId: string | null;
  managerId: string;
  logistId: string | null;
}

// Mirrors `RequestsService.canAuthorRequest` in
// `apps/backend/src/modules/requests/requests.service.ts`. A custom or
// stale-FK role can match `managerId` without holding the canonical
// author-identity capability — e.g. a manager demoted to logist_cn whose
// old DRAFTs still carry their stale `managerId`. Such a row must NOT
// expose Send / Close / pickWinner CTAs even though the party id
// matches; the backend's lockVisibleOrThrow + pickWinner path rejects
// these writes, so the client gate must too.
function canAuthor(perms: PermissionsBundle): boolean {
  if (perms === 'ALL') return true;
  return hasPermission(perms, 'requests.transition.send');
}

// Mirrors `RequestsService.canActAsLogist`. A scope='all' actor with
// requests.scope.all but no requests.transition.process is the dual
// case of canAuthor — visibility ≠ write authority — and must lose
// logist-side actions even on a row whose `logistId` matches them.
function canActLogist(perms: PermissionsBundle): boolean {
  if (perms === 'ALL') return true;
  return hasPermission(perms, 'requests.transition.process');
}

export function isPartyAllowed(
  side: 'manager' | 'logist' | 'either',
  permissions: PermissionsBundle,
  ctx: ActorPartyContext,
): boolean {
  if (permissions === 'ALL') return true;
  if (ctx.actorUserId === null) return false;
  // Side-capability check is mandatory, not just party-id match —
  // see `canAuthor` / `canActLogist` comments for the cases this
  // closes (custom/demoted roles, stale FKs).
  const managerAllowed = ctx.actorUserId === ctx.managerId && canAuthor(permissions);
  const logistAllowed =
    ctx.logistId !== null && ctx.actorUserId === ctx.logistId && canActLogist(permissions);
  if (side === 'manager') return managerAllowed;
  if (side === 'logist') return logistAllowed;
  return managerAllowed || logistAllowed;
}

// INVARIANT (#962, widened #2099/#2135): mirrors the per-side `return`
//   narrowing in `transition-orchestrator.ts:runTransition`. The matrix
//   `return.from` fences to the four active states {READY_FOR_WORK,
//   IN_WORK, PROCESSED, POSTPONED}; this clause splits authority per side.
//   A MANAGER may return from any matrix-legal source (the four-state
//   union is already enforced by the `def.from` check in `allowedActions`).
//   A LOGIST may return only from the two pre-decision active states
//   {READY_FOR_WORK, IN_WORK} — once the row reaches PROCESSED/POSTPONED
//   the correction call is the manager's.
function isReturnAllowed(
  status: RequestStatus,
  permissions: PermissionsBundle,
  ctx: ActorPartyContext,
): boolean {
  if (permissions === 'ALL') return true;
  if (ctx.actorUserId === null) return false;
  const managerAllowed = ctx.actorUserId === ctx.managerId && canAuthor(permissions);
  const logistAllowed =
    ctx.logistId !== null &&
    ctx.actorUserId === ctx.logistId &&
    canActLogist(permissions) &&
    (status === RequestStatus.READY_FOR_WORK || status === RequestStatus.IN_WORK);
  return managerAllowed || logistAllowed;
}

export function allowedActions(
  status: RequestStatus,
  permissions: PermissionsBundle,
  party: ActorPartyContext,
  facts?: RequestFacts,
): TransitionAction[] {
  const out: TransitionAction[] = [];
  for (const key of Object.keys(REQUEST_TRANSITIONS) as TransitionKey[]) {
    const t = REQUEST_TRANSITIONS[key];
    const fromList = t.from as readonly RequestStatus[];
    if (!fromList.includes(status)) continue;
    const permOk = (t.permissions as readonly PermissionCode[]).every((p) =>
      hasPermission(permissions, p),
    );
    if (!permOk) continue;
    if (key === 'return') {
      if (!isReturnAllowed(status, permissions, party)) continue;
    } else if (key === 'archive' && status === RequestStatus.CANNOT_TRANSPORT) {
      // INVARIANT (#2136/D-9): a CANNOT_TRANSPORT row is the assigned logist's
      //   terminal; the owning manager is locked out of archiving it. Narrow
      //   the 'either' side to logist (admin bypass preserved by isPartyAllowed).
      if (!isPartyAllowed('logist', permissions, party)) continue;
    } else if (!isPartyAllowed(TRANSITION_SIDE[key], permissions, party)) {
      continue;
    }
    // INVARIANT: `close` stays in `REQUEST_TRANSITIONS` and on the
    // backend for compatibility, but normal request-finalisation UI
    // routes managers through `success` / `refuse` from PROCESSED.
    // Surfacing close here would race the new flow and produce
    // ambiguous audit history (close vs. transition.success/refuse).
    if (key === 'close') continue;
    // #1164: the `process` action stays visible in IN_WORK but
    //   becomes disabled with a tooltip when no carrier has answered.
    //   Mirrors the backend ANSWERED_CARRIER_REQUIRED gate.
    if (
      key === 'process' &&
      status === RequestStatus.IN_WORK &&
      facts !== undefined &&
      facts.hasAnsweredCarrier === false
    ) {
      out.push({
        key,
        to: t.to,
        ...ACTION_META[key],
        disabled: true,
        disabledReasonKey: 'actions.processDisabledNoAnswered',
      });
      continue;
    }
    out.push({ key, to: t.to, ...ACTION_META[key] });
  }
  return out;
}
